import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import AuthLayout from "@/components/layout/AuthLayout";
import { Loader2, MailCheck, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const { t } = useT();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/api/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <h1 className="text-2xl font-bold tracking-tight text-slate-900">{t("auth.forgot.title")}</h1>
      <p className="mt-1.5 text-sm text-slate-500">{t("auth.forgot.subtitle")}</p>

      {sent ? (
        <div className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-center" data-testid="forgot-success">
          <MailCheck className="mx-auto h-8 w-8 text-primary" />
          <p className="mt-3 text-sm text-emerald-800">{t("auth.forgot.sent")}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
          {error && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">{error}</div>
          )}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">{t("auth.email")}</label>
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="forgot-email-input" placeholder="vous@entreprise.com"
              className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <button
            type="submit" disabled={busy} data-testid="forgot-submit-button"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-60"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("auth.forgot.cta")}
          </button>
        </form>
      )}

      <Link to="/login" data-testid="back-to-login-link" className="mt-8 flex items-center justify-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> {t("auth.back_login")}
      </Link>
    </AuthLayout>
  );
}
